/**
 * Debug logger that captures logs for easy export
 */
export class DebugLogger {
  private static logs: string[] = []
  private static maxLogs = 1000
  private static isCapturing = false

  /**
   * Start capturing console logs
   */
  static startCapture(): void {
    if (this.isCapturing) return

    this.isCapturing = true
    this.logs = []

    // Intercept console.log
    const originalLog = console.log
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    console.log = (...args: any[]) => {
      const timestamp = new Date().toISOString()
      const message = args
        .map((arg) =>
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg),
        )
        .join(' ')

      this.logs.push(`[${timestamp}] ${message}`)

      // Keep only last N logs
      if (this.logs.length > this.maxLogs) {
        this.logs.shift()
      }

      // Call original console.log
      originalLog.apply(console, args)
    }

    console.log('🎬 Debug logger started - logs will be captured')
  }

  /**
   * Stop capturing and return logs
   */
  static stopCapture(): string[] {
    this.isCapturing = false
    return [...this.logs]
  }

  /**
   * Download logs as a text file
   */
  static downloadLogs(filename: string = 'debug-logs.txt'): void {
    const content = this.logs.join('\n')
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()

    URL.revokeObjectURL(url)
    console.log(`📥 Logs downloaded as ${filename}`)
  }

  /**
   * Get recent logs matching a pattern
   */
  static filter(pattern: string | RegExp): string[] {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern
    return this.logs.filter((log) => regex.test(log))
  }

  /**
   * Clear captured logs
   */
  static clear(): void {
    this.logs = []
    console.log('🗑️ Logs cleared')
  }

  /**
   * Get log summary
   */
  static summary(): void {
    console.log(`📊 Debug Logger Summary:`)
    console.log(`  Total logs: ${this.logs.length}`)
    console.log(`  Capturing: ${this.isCapturing}`)

    // Count by prefix
    const counts: Record<string, number> = {}
    this.logs.forEach((log) => {
      const match = log.match(
        /\[(No\.\d+|SETUP|CONNECT|START-TURN|START-SESSION|INSTANCE|FILTERED)\]/,
      )
      if (match) {
        const prefix = match[1]
        counts[prefix] = (counts[prefix] || 0) + 1
      }
    })

    console.log('  Breakdown by prefix:')
    Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .forEach(([prefix, count]) => {
        console.log(`    ${prefix}: ${count}`)
      })
  }
}

// Make it available globally for easy console access
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).debugLogger = DebugLogger
}
